/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useLocation, useParams } from 'react-router';
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import SecurityOutlined from '@mui/icons-material/SecurityOutlined';
import { Box, Divider, styled, Typography } from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import type { MRT_ColumnDef } from 'material-react-table';

import { ReactComponent as ReleaseNotes } from '~/release.md';
import useSession from '~/hooks/useSession/useSession';
import { useVersionNotificationMutation } from '~/hooks/mutations/useVersionNotificationMutation/index';
import { ClientsService } from '~/services/ClientsService';
import useGetClientSessionsQuery from '~/hooks/queries/useGetClientSessionsQuery';
import useSessionPlQuery from '~/hooks/queries/useSessionPlQuery';
import { SetNotification } from '~/context/Notifications';
import defaultAvatar from '~/assets/img/placeholder.jpg';
import dialog from '../ActionConfirmDialog/useDialog';
import DataTable from '../DataTable/DataTable';
import CustomLink from '../Link/Link';
import SessionTimer from './SessionTimer';
import { accountMenuItems, getVisibleMenuItems, buildMenuItemPath } from '../Sidebar/UserMenuItems';

import packagejson from '../../../../package.json';
import styles from './styles';

const StyledCustomMenuLink = styled(CustomLink)({
  ...styles.link,
  ...styles.menuLink,
});

interface AdminUserLinksProps {
  setNotification: SetNotification;
  closeNotification: () => void;
  sidebarOpen?: boolean;
  isAuth?: boolean;
}

interface SessionData {
  country_name: string;
  ipaddress: string;
  stamp: string;
  status: string;
}

interface ClientSessionsData {
  sessions?: SessionData[];
}

const columns: MRT_ColumnDef<SessionData>[] = [
  {
    header: 'Country',
    accessorKey: 'country_name',
  },
  {
    header: 'IP Address',
    accessorKey: 'ipaddress',
  },
  {
    header: 'Timestamp',
    accessorKey: 'stamp',
  },
  {
    header: 'Status',
    accessorKey: 'status',
  },
];

function UserLinks({
  setNotification,
  closeNotification,
  sidebarOpen = false,
  isAuth = false,
}: AdminUserLinksProps) {
  const { logout } = useSession();
  const [openUserOptions, setOpenUserOptions] = useState(false);
  const { data: sessionPlData } = useSessionPlQuery();
  const toggleRef = useRef<HTMLAnchorElement>(null);
  const location = useLocation();
  const { accountType, accountId } = useParams();

  const { data, isLoading } = useGetClientSessionsQuery();
  const sessionsData: ClientSessionsData = data ?? { sessions: [] };
  const sessionsCount = sessionsData.sessions ? sessionsData.sessions.length : 0;

  const closeMenu = () => setOpenUserOptions(false);

  const onLogoutClick = () => {
    closeMenu();
    logout(500);
  };

  const handleOpenWhatsNew = useCallback(async () => {
    try {
      dialog({
        title: "What's New on MyDianomi",
        body: <ReleaseNotes />,
        cancelText: 'Close',
        width: 'md',
        onClose: () => dialog.close(),
        onOpen: null,
        showCloseButton: true,
      });
      closeNotification();
    } catch (err) {
      Sentry.captureException(err);
    }
  }, [closeNotification]);

  const handleCloseAllSessions = async () => {
    const result = await ClientsService.closeClientSessions();
    if (result && typeof result === 'object' && 'logged_out' in result && result.logged_out === 1) {
      logout(1000);
    }
    dialog.close();
  };

  const handleSessionClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const sessions = sessionsData.sessions || [];
    const sessionsTable = (
      <Box sx={styles.sessionTableWrapper}>
        <DataTable
          data={sessions}
          columns={columns}
          enableColumnActions={false}
          enableColumnFilters={false}
          enablePagination={false}
          enableBottomToolbar={false}
          enableTopToolbar={false}
          muiTableBodyRowProps={{ hover: false }}
        />
      </Box>
    );
    dialog({
      title: 'Active Sessions',
      body: sessions.length ? sessionsTable : <p>Session data unavailable</p>,
      acceptText: 'Close All Sessions',
      width: 'md',
      onClose: () => dialog.close(),
      onAccept: handleCloseAllSessions,
    });
    closeMenu();
  };

  const handleReleaseNotesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    handleOpenWhatsNew();
    closeMenu();
  };

  // handle version mismatch notification and cookie update
  useVersionNotificationMutation({
    onNotify: () => {
      setNotification(
        true,
        'primary',
        'New update to the MyDianomi dashboard!',
        'top-center',
        handleOpenWhatsNew,
        "See what's new",
      );
    },
  });

  const userName = sessionPlData?.login?.name ?? 'User';
  const userNameInitials =
    userName
      ?.split(' ')
      .map((word) => word.slice(0, 1))
      .join('')
      .toUpperCase() || '';

  const login = sessionPlData?.login;
  const userLogo =
    login && 'logo' in login && typeof login.logo === 'string' ? login.logo : defaultAvatar;
  const orgName =
    login && 'company_name' in login && typeof login.company_name === 'string'
      ? login.company_name
      : null;

  if (isAuth) {
    return (
      <Box
        component="p"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          color: 'text.primary',
          gap: '5px',
          borderTop: '1px solid #e0e0e0',
          paddingTop: '20px',
        }}
      >
        &copy;
        {new Date().getFullYear()}
        <Box component="a" href="https://www.dianomi.com" sx={{ color: 'text.primary' }}>
          Dianomi
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.navbarWrapper}>
      {/* User Dropdown Menu */}
      <ClickAwayListener onClickAway={closeMenu}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <SessionTimer />

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <List sx={{ width: '100% !important', padding: 0, position: 'relative' }}>
              <ListItem sx={styles.userItem}>
                <StyledCustomMenuLink
                  to={`/${accountType}/${accountId}/user-profile`}
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    setOpenUserOptions(!openUserOptions);
                  }}
                  sx={{
                    width: '100%',
                    height: '50px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: sidebarOpen ? 'space-between' : 'center',
                    padding: ' 4px',
                  }}
                  ref={toggleRef}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                    {userLogo === defaultAvatar ? (
                      <Typography
                        sx={{
                          fontSize: '12px',
                          fontWeight: 600,
                          color: 'text.primary',
                          backgroundColor: 'secondary.main',
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {userNameInitials}
                      </Typography>
                    ) : (
                      <img src={userLogo} style={styles.avatarImg} alt={userName} />
                    )}
                    {sidebarOpen && (
                      <Box sx={{ ml: 1, minWidth: 0, overflow: 'hidden' }}>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: 'text.primary',
                            lineHeight: 1.3,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {userName}
                        </Typography>
                        {orgName && (
                          <Typography
                            sx={{
                              fontSize: '10px',
                              color: 'text.secondary',
                              lineHeight: 1.2,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {orgName}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                  {sidebarOpen && (
                    <UnfoldMore
                      sx={{
                        fontSize: '16px',
                        transform: 'scale(1)',
                        transition: 'transform 0.2s ease',
                        ':hover': {
                          transform: 'scale(1.2)',
                        },
                        color: 'primary.main',
                        flexShrink: 0,
                      }}
                    />
                  )}
                </StyledCustomMenuLink>
              </ListItem>

              {/* account menu - popover panel */}
              <Collapse in={openUserOptions} unmountOnExit sx={styles.collapse}>
                <Box
                  sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: '4px 4px 0px 0px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                    overflow: 'hidden',
                    minWidth: '210px',
                    zIndex: 1000,
                  }}
                >
                  <List sx={{ py: 0.5 }}>
                    {/* shared menu items (staff, getting started, sign out) */}
                    {getVisibleMenuItems(
                      accountMenuItems.filter((item) => item.id !== 'releaseNotes'),
                      {
                        isDianomi: !!sessionPlData?.login?.is_dianomi,
                        isClientAccount: accountType === 'client',
                        currentPath: location.pathname,
                      },
                    ).map((item) => {
                      const IconComponent = item.icon;

                      // active sessions
                      if (item.id === 'signOut') {
                        return (
                          <React.Fragment key="signout-with-sessions">
                            <ListItem
                              component="button"
                              onClick={handleSessionClick}
                              sx={styles.primaryMenuItem}
                            >
                              <ListItemIcon sx={styles.menuIcon}>
                                <SecurityOutlined fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary="Active Sessions"
                                slotProps={{
                                  primary: {
                                    sx: {
                                      fontSize: '13px',
                                      fontWeight: 500,
                                    },
                                  },
                                }}
                              />
                              {!isLoading && (
                                <Box
                                  sx={{
                                    px: '8px',
                                    py: '2px',
                                    borderRadius: '50%',
                                    backgroundColor: 'warning.light',
                                    color: 'secondary.main',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                  }}
                                >
                                  {sessionsCount}
                                </Box>
                              )}
                            </ListItem>
                            {/* Sign Out */}
                            <ListItem
                              component="button"
                              onClick={onLogoutClick}
                              sx={styles.primaryMenuItem}
                            >
                              <ListItemIcon sx={styles.menuIcon}>
                                <IconComponent fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary={item.label}
                                slotProps={{
                                  primary: {
                                    sx: {
                                      fontSize: '13px',
                                      fontWeight: 500,
                                    },
                                  },
                                }}
                              />
                            </ListItem>
                          </React.Fragment>
                        );
                      }

                      // link items (staff, getting started)
                      if (item.pathTemplate) {
                        const path = buildMenuItemPath(
                          item.pathTemplate,
                          accountType ?? '',
                          accountId ?? '',
                        );
                        return (
                          <ListItem
                            key={item.id}
                            component={CustomLink}
                            to={{ ...location, pathname: path }}
                            onClick={closeMenu}
                            sx={styles.primaryMenuItem}
                          >
                            <ListItemIcon sx={styles.menuIcon}>
                              <IconComponent fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={item.id === 'staff' ? 'Staff' : item.label}
                              slotProps={{
                                primary: {
                                  sx: {
                                    fontSize: '13px',
                                    fontWeight: 500,
                                  },
                                },
                              }}
                            />
                          </ListItem>
                        );
                      }

                      return null;
                    })}
                  </List>

                  <Divider sx={{ my: 0.5 }} />

                  {/* secondary information section - de-emphasised */}
                  <List sx={{ py: 0.5 }}>
                    {/* release notes - using shared config for icon */}
                    {(() => {
                      const releaseNotesItem = accountMenuItems.find(
                        (i) => i.id === 'releaseNotes',
                      );
                      const ReleaseNotesIcon = releaseNotesItem?.icon;
                      return ReleaseNotesIcon ? (
                        <ListItem
                          component="button"
                          onClick={handleReleaseNotesClick}
                          sx={styles.secondaryMenuItem}
                        >
                          <ListItemIcon sx={styles.secondaryMenuIcon}>
                            <ReleaseNotesIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={releaseNotesItem.label}
                            slotProps={{
                              primary: {
                                sx: {
                                  fontSize: '12px',
                                  color: 'text.secondary',
                                },
                              },
                            }}
                          />
                        </ListItem>
                      ) : null;
                    })()}

                    {/* app version */}
                    <ListItem
                      sx={{
                        ...styles.secondaryMenuItem,
                        display: 'flex',
                        justifyContent: 'end',
                        alignItems: 'end',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          cursor: 'default',
                        },
                      }}
                    >
                      <ListItemText
                        primary={`Version ${packagejson.version}`}
                        slotProps={{
                          primary: {
                            sx: {
                              fontSize: '10px',
                              color: 'text.secondary',
                            },
                          },
                        }}
                      />
                    </ListItem>
                  </List>
                </Box>
              </Collapse>
            </List>
          </Box>
        </Box>
      </ClickAwayListener>
    </Box>
  );
}

export default UserLinks;
